import { useEffect, useRef, useState } from "react";
import { ChevronDown, RefreshCw } from "lucide-react";
import type { MindsetIntegrationAuditPage } from "@mindset/api-client";
import type { IntegrationSetupClient, IntegrationProvisioningClient } from "./integrationSetupClient";
import { IntegrationOperationStatus } from "./IntegrationOperationStatus";

interface Props { companyId: string; userId: string; client: Pick<IntegrationSetupClient, "activity"> & Partial<Pick<IntegrationProvisioningClient, "getProvisioning">> }
const eventLabels = new Map([
  ["DRAFT_CREATED", { title: "Application draft created", resource: "Application" }],
  ["APPLICATION_OWNERSHIP_REPLACED", { title: "Application ownership updated", resource: "Application" }],
  ["CHECKOUT_REQUESTED", { title: "Subscription checkout requested", resource: "Checkout operation" }],
  ["CHECKOUT_SESSION_CREATED", { title: "Hosted checkout session created", resource: "Checkout operation" }],
  ["CHECKOUT_SESSION_RECOVERED", { title: "Hosted checkout session recovered", resource: "Checkout operation" }],
  ["CHECKOUT_SUBSCRIPTION_BOUND", { title: "Payment verified", resource: "Checkout operation" }],
  ["CHECKOUT_EXPIRED", { title: "Checkout expired", resource: "Checkout operation" }],
  ["DOMAIN_CHALLENGE_ISSUED", { title: "DNS verification requested", resource: "DNS challenge" }],
  ["DOMAIN_CHALLENGE_VERIFIED", { title: "Domain ownership verified", resource: "DNS challenge" }],
  ["DOMAIN_OWNERSHIP_RENEWED", { title: "Domain ownership renewed", resource: "DNS challenge" }],
  ["HOST_ACCESS_POLICY_REGISTERED", { title: "Host access registered", resource: "Host access policy" }],
  ["HOST_ACCESS_POLICY_VERIFIED", { title: "Host access verified", resource: "Host access policy" }],
  ["ANDROID_CHALLENGE_ISSUED", { title: "Android ownership requested", resource: "Android challenge" }],
  ["ANDROID_DAL_OBSERVED", { title: "Android app link observed", resource: "Android challenge" }],
  ["ANDROID_AUTHORITY_VERIFIED", { title: "Android authority verified", resource: "Android challenge" }],
  ["ANDROID_CHALLENGE_REVOKED", { title: "Android ownership revoked", resource: "Android challenge" }],
  ["PROVISIONING_REQUESTED", { title: "Tenant activation requested", resource: "Provisioning operation" }],
  ["PROVISIONING_OWNER_SUPERSEDED", { title: "Tenant activation reassigned", resource: "Provisioning operation" }],
  ["RUNTIME_GRANT_STAGED", { title: "Tenant configuration staged", resource: "Runtime grant" }],
  ["RUNTIME_GRANT_SUPERSEDED", { title: "Prior tenant configuration superseded", resource: "Runtime grant" }],
  ["RUNTIME_GRANT_ACTIVATED", { title: "Tenant activation completed", resource: "Runtime grant" }],
]);
export function IntegrationActivity(props: Props) {
  return <Activity key={`${props.companyId}:${props.userId}`} {...props} />;
}
function Activity({ companyId, userId, client }: Props) {
  const [page, setPage] = useState<MindsetIntegrationAuditPage>({ events: [], nextCursor: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const generation = useRef(0);
  const pending = useRef(false);
  useEffect(() => {
    const current = ++generation.current;
    pending.current = true; setBusy(true); setError("");
    setPage({ events: [], nextCursor: null });
    void client.activity(companyId, userId, { limit: 20 }).then((result) => {
      if (current === generation.current) setPage(result);
    }, () => { if (current === generation.current) setError("Activity could not be loaded."); })
      .finally(() => { if (current === generation.current) { pending.current = false; setBusy(false); } });
    return () => { ++generation.current; };
  }, [companyId, userId, client, version]);
  async function older() {
    if (pending.current || !page.nextCursor) return;
    const current = generation.current;
    pending.current = true; setBusy(true); setError("");
    try {
      const result = await client.activity(companyId, userId, { limit: 20, ...page.nextCursor });
      if (current !== generation.current) return;
      setPage((previous) => {
        const ids = new Set(previous.events.map((event) => event.id));
        return { events: [...previous.events, ...result.events.filter((event) => !ids.has(event.id))], nextCursor: result.nextCursor };
      });
    } catch { if (current === generation.current) setError("Older activity could not be loaded. Try again."); }
    finally { if (current === generation.current) { pending.current = false; setBusy(false); } }
  }
  return <section className="integration-activity" aria-labelledby="integration-activity-title">
    <header className="integration-applications-heading"><h3 id="integration-activity-title">Activity</h3>
      <button type="button" className="portal-icon-button" title="Refresh activity" aria-label="Refresh activity" disabled={busy} onClick={() => setVersion((value) => value + 1)}><RefreshCw size={17} /></button>
    </header>
    {error ? <p role="alert">{error}</p> : null}
    {busy ? <p role="status">Loading activity...</p> : null}
    {!busy && !error && !page.events.length ? <p>No integration activity.</p> : null}
    <ol className="integration-activity-list">{page.events.map((event) => <li key={event.id}>
      <strong>{eventLabels.get(event.action)?.title ?? "Integration event"}</strong>
      <time dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString()}</time>
      <span>{eventLabels.get(event.action)?.resource ?? "Resource"}: {event.resourceId}</span><span>Actor: {event.actorUserId}</span>
      {event.action === "PROVISIONING_REQUESTED" && client.getProvisioning ? <IntegrationOperationStatus companyId={companyId} userId={userId}
        operationId={event.resourceId} read={client.getProvisioning} /> : null}
    </li>)}</ol>
    {page.nextCursor ? <button type="button" className="portal-button" disabled={busy} onClick={() => void older()}><ChevronDown size={16} />Older activity</button> : null}
  </section>;
}
